import { computed, onMounted, onUnmounted, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import {
  calculateApplicablePromotions,
  calculateCheckoutChargeAmount,
  calculateLegScopedRewards,
  evaluateLoyaltySelection,
  isLoyaltyPromotion,
  normalizeLoyaltyRewardSelections,
} from "@lemmonhq/orders";
import type {
  CheckoutData,
  GiftCard,
  LoyaltyRewardSelection,
  Promotion,
} from "@lemmonhq/types";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth, useCheckout, useShop } from "../../../providers";
import type {
  CheckoutCustomerPreview,
  LoyaltyModalProps,
  ScanType,
} from "./types";

export function useLoyaltyModal(props: LoyaltyModalProps) {
  const { t } = useLocalization();
  const auth = useAuth();
  const checkout = useCheckout();
  const shop = useShop();
  const trpc = useTrpc();
  const mode = ref<ScanType>(props.initialScanType ?? "membership");
  const preview = ref<CheckoutCustomerPreview | null>(null);
  const selected = ref<LoyaltyRewardSelection[]>([]);
  const email = ref("");
  const giftCode = ref("");
  const card = ref<GiftCard | null>(null);
  const amount = ref("");
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<string | null>(null);

  const mobile = ref(window.innerWidth < 768);
  let request = 0;
  let disposed = false;
  const currency = computed(
    () => auth.user.value?.restaurant.currency ?? "CHF",
  );
  const money = (n: number) => `${currency.value} ${n.toFixed(2)}`;
  const allPromotions = computed(
    () => (shop.getPromotions() ?? []) as Promotion[],
  );
  const promotions = computed(() =>
    allPromotions.value.filter((promotion) => promotion.enabled),
  );
  const existing = computed(() => checkout.checkout.value?.customer);
  const draft = computed(
    () =>
      ({
        ...checkout.checkout.value,
        customer: preview.value
          ? {
              ...preview.value,
              id: existing.value?.id ?? preview.value.profile_id,
              reward_selections: selected.value,
            }
          : undefined,
      }) as CheckoutData,
  );
  const ordinary = computed(() => {
    const ids = new Set(
      allPromotions.value.filter(isLoyaltyPromotion).map((p) => p.id),
    );
    return (checkout.checkout.value?.promotions ?? []).filter(
      (p) => !ids.has(p.promotion_id),
    );
  });
  const evaluation = computed(() =>
    evaluateLoyaltySelection(
      shop.services.value,
      draft.value,
      promotions.value,
      selected.value,
      ordinary.value,
    ),
  );
  const payable = computed(() =>
    calculateCheckoutChargeAmount({
      ...draft.value,
      promotions: [...ordinary.value, ...evaluation.value.applications],
    }),
  );
  const displayOptions = computed(() =>
    evaluation.value.options.map((option) => ({
      ...option,
      value:
        calculateLegScopedRewards({
          ...draft.value,
          promotions: option.applications,
        })?.total ?? 0,
    })),
  );
  const savings = computed(
    () =>
      calculateLegScopedRewards({
        ...draft.value,
        promotions: evaluation.value.applications,
      })?.total ?? 0,
  );
  const missingSelections = computed(() =>
    selected.value.filter(
      (selection) =>
        !evaluation.value.options.some(
          (o) => o.promotion.id === selection.promotion_id,
        ),
    ),
  );
  const invalidSelection = computed(
    () =>
      missingSelections.value.length > 0 ||
      evaluation.value.options.some(
        (o) =>
          selected.value.some(
            (selection) => selection.promotion_id === o.promotion.id,
          ) && o.reason,
      ),
  );
  const close = () => {
    if (!saving.value) {
      props.onClose();
    }
  };
  const reset = () => {
    request++;
    preview.value = null;
    card.value = null;
    amount.value = "";
    selected.value = [];
    error.value = null;
    loading.value = false;
  };
  const acceptPreview = (result: CheckoutCustomerPreview) => {
    preview.value = result;
    if (result.profile_id === existing.value?.profile_id) {
      const enabledIds = new Set(
        promotions.value.map((promotion) => promotion.id),
      );
      selected.value = normalizeLoyaltyRewardSelections(
        existing.value.reward_selections ??
          (checkout.checkout.value?.promotions ?? [])
            .filter((a) =>
              promotions.value.some(
                (p) => p.id === a.promotion_id && isLoyaltyPromotion(p),
              ),
            )
            .map((a) => a.promotion_id),
      ).filter((selection) => enabledIds.has(selection.promotion_id));
    } else {
      selected.value = normalizeLoyaltyRewardSelections([
        ...new Set(
          calculateApplicablePromotions(
            shop.services.value,
            {
              ...draft.value,
              customer: { ...result, id: result.profile_id },
            },
            promotions.value.filter(isLoyaltyPromotion),
          )
            .filter((p) => p.value > 0)
            .map((p) => p.promotion_id),
        ),
      ]);
    }
  };
  const resolve = async (profileId?: string) => {
    const current = ++request;
    loading.value = true;
    error.value = null;
    try {
      const result = profileId
        ? await trpc.coinPrograms.resolveCheckoutCustomer.mutate({
            profile_id: profileId,
          })
        : await trpc.coinPrograms.resolveCheckoutCustomerByEmail.mutate({
            email: email.value.trim(),
          });
      if (current === request && !disposed)
        acceptPreview({
          ...result,
          display_name:
            result.display_name ?? (profileId ? undefined : email.value.trim()),
        });
    } catch {
      if (current === request && !disposed)
        error.value = t("loyalty.modal.resolve_error");
    } finally {
      if (current === request && !disposed) loading.value = false;
    }
  };
  const lookupGift = async (code: string) => {
    amount.value = "";
    const current = ++request;
    loading.value = true;
    error.value = null;
    card.value = null;
    try {
      const result = await trpc.giftCards.lookupByCode.query({ code });
      if (current === request && !disposed) card.value = result;
    } catch {
      if (current === request && !disposed)
        error.value = t("loyalty.rewards.gift_error");
    } finally {
      if (current === request && !disposed) loading.value = false;
    }
  };
  const onScan = (data: string) => {
    const gift = data.match(/\/gift_cards\/([^/?#]+)/);
    const member = data.match(/^lemmon-loyalty:(.+)$/);
    if (gift || (!member && mode.value === "gift_card")) {
      mode.value = "gift_card";
      lookupGift(gift?.[1] ?? data);
    } else {
      mode.value = "membership";
      resolve(member?.[1].trim() ?? data);
    }
  };
  const applyAmount = computed(() => {
    const manual = Number(amount.value);
    return Math.max(
      0,
      Math.min(
        Number(card.value?.current_balance ?? 0),
        props.remainingTotal ?? props.totalAmount,
        amount.value ? (Number.isFinite(manual) ? manual : 0) : Infinity,
      ),
    );
  });
  const save = async () => {
    if (saving.value || loading.value) return;
    if (mode.value === "gift_card") {
      if (card.value?.status === "active" && applyAmount.value > 0) {
        props.onApply?.(card.value.code, applyAmount.value);
        close();
      }
      return;
    }
    if (!preview.value || invalidSelection.value) return;
    saving.value = true;
    error.value = null;
    try {
      const result = await trpc.coinPrograms.attachCheckoutCustomer.mutate({
        profile_id: preview.value.profile_id,
      });
      const fresh = await trpc.coinPrograms.resolveCheckoutCustomer.mutate({
        profile_id: preview.value.profile_id,
      });
      const previousValue = evaluation.value.value;
      preview.value = fresh;
      if (
        invalidSelection.value ||
        Math.abs(evaluation.value.value - previousValue) > 0.005
      ) {
        error.value = t("loyalty.rewards.availability_changed");
        return;
      }
      props.onCustomerResolved?.({
        ...result,
        ...fresh,
        profile_id: preview.value.profile_id,
        display_name: preview.value.display_name,
        memberships: preview.value.memberships,
        reward_selections: [...selected.value],
      });
      props.onClose();
    } catch {
      error.value = t("loyalty.modal.resolve_error");
    } finally {
      saving.value = false;
    }
  };
  const resize = () => {
    mobile.value = window.innerWidth < 768;
  };
  onMounted(() => {
    window.addEventListener("resize", resize);
    if (mode.value === "membership" && existing.value?.profile_id)
      resolve(existing.value.profile_id);
  });
  onUnmounted(() => {
    disposed = true;
    request++;
    window.removeEventListener("resize", resize);
  });

  return {
    t,
    mode,
    reset,
    existing,
    resolve,
    preview,
    loading,
    onScan,
    error,
    email,
    giftCode,
    lookupGift,
    card,
    amount,
    currency,
    money,
    applyAmount,
    selected,
    displayOptions,
    savings,
    payable,
    saving,
    save,
    close,
    mobile,
    missingSelections,
    invalidSelection,
    draft,
    services: shop.services,
  };
}
